import { Box, Flex, Text, HStack, VStack, useToast, Icon } from '@chakra-ui/react';
import React, { useState } from 'react';
import { useCallback, useEffect } from 'react';
import { getCurrentToken, handleAuthentication, logout } from '../../services/Admin/AuthService';
import { fetchOwnBookings } from '../../services/Admin/OwnerService';
import { OwnBookingsBookingsData, OwnBookingsResponse } from '../../types/AdminTypes';

import { ObjectsParams } from '../../types/ParamsTypes';
import { getDefaultObjectsParams } from '../../services/Objects/ObjectsService';
import { TopNavigation } from '../../components/Admin/TopNavigation/TopNavigation';
import { SelectObject } from '../../components/Admin/Dashboard/SelectObject';
import { YearPicker } from '../../components/Admin/Dashboard/YearPicker';
import { BookingBigCalendar } from '../../components/Admin/Dashboard/BookingBigCalendar';
import { BookingsForm } from '../../components/Admin/Dashboard/BookingsForm/BookingsForm';
import { TableBookings } from '../../components/Admin/Dashboard/TableBookings';
import { MonthPicker } from '../../components/Admin/Dashboard/MonthPicker';
import { useHistory } from 'react-router-dom';
import { BookingEditParams } from '../../types/BookingTypes';
import { start } from 'repl';

interface DashboardViewProps {
  handleLoadingSpinner: (value: boolean) => void;
  handleTokenState: (token: string | null) => void;
}

export const DashboardView = (props: DashboardViewProps): JSX.Element => {
  const { handleLoadingSpinner, handleTokenState } = props;
  const toast = useToast();

  const [ownBookingsData, setOwnBookingsData] = useState<OwnBookingsResponse>();
  const [objectSelected, setObjectSelected] = useState<OwnBookingsBookingsData>();
  const [dirtyObjects, setDirtyObjects] = useState<boolean>(false);

  const [objectsParams, setObjectsParams] = useState<ObjectsParams>(getDefaultObjectsParams());
  const [bookingEditParams, setBookingEditParams] = useState<BookingEditParams>({
    BookingComment: '',
    booking: '',
    initialMonth: undefined,
  });

  const handleBookingEditParams = useCallback(
    (params: BookingEditParams): void => {
      setBookingEditParams(params);
    },
    [bookingEditParams],
  );

  const syncBigCalendarWithBookingDate = useCallback((updatedObjectsParams: ObjectsParams) => {
    const startDate = updatedObjectsParams.fromDate
      ? new Date(updatedObjectsParams.fromDate)
      : new Date();

    const startYear = startDate.getFullYear();
    const startMonth = startDate.getMonth();

    setSelectedYear(startYear);
    setSelectedMonth(startMonth);
  }, []);

  const handleDateRange = useCallback(
    (updatedObjectsParams: ObjectsParams) => {
      setObjectsParams(updatedObjectsParams);
      //syncBigCalendarWithBookingDate(updatedObjectsParams);
    },
    [setObjectsParams],
  );

  const ADMIN_DETAILS = 'ADMIN_DETAILS';
  const START_YEAR = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState<number>(START_YEAR);

  const START_MONTH = new Date().getMonth();
  const [selectedMonth, setSelectedMonth] = useState<number>(START_MONTH);

  useEffect(() => {
    if (!objectSelected && ownBookingsData) {
      const ownBookingsDataArr = ownBookingsData?.data.result.bookingsData;
      setObjectSelected(ownBookingsDataArr[0]);
    }
  }, [ownBookingsData]);

  const handleObjectSelected = (object: OwnBookingsBookingsData) => {
    setObjectSelected({ ...object });
  };

  const handleSelectedYear = useCallback(
    (year: number) => {
      setSelectedYear(year);
      setBookingEditParams({ ...bookingEditParams, initialMonth: new Date(year, selectedMonth) });
    },
    [setSelectedYear, setBookingEditParams, bookingEditParams, selectedMonth],
  );

  const handleSelectedMonth = useCallback(
    (month: number) => {
      setSelectedMonth(month);
      setBookingEditParams({ ...bookingEditParams, initialMonth: new Date(selectedYear, month) });
    },
    [selectedYear, bookingEditParams, setSelectedMonth, setBookingEditParams],
  );

  const handleDirtyObjects = (value: boolean) => {
    setDirtyObjects(value);
  };

  useEffect(() => {
    handleLoadingSpinner(false);
  }, []);

  const handleLogout = useCallback(() => {
    (async function () {
      try {
        const token = getCurrentToken(ADMIN_DETAILS);
        await logout(token);
      } finally {
        sessionStorage.removeItem(ADMIN_DETAILS);
        handleTokenState(null);
        toast({
          position: 'top-right',
          title: 'Sie wurden erfolgreich abgemeldet.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      }
    })();
  }, []);

  useEffect(() => {
    (async function () {
      try {
        await handleAuthentication(ADMIN_DETAILS, handleTokenState);

        const token = getCurrentToken(ADMIN_DETAILS);
        const ownBookings = await fetchOwnBookings(token);

        setOwnBookingsData(ownBookings.data);
      } catch {
        toast({
          position: 'top-right',
          title: 'Objekte konnten nicht geladen werden.',
          status: 'error',
          duration: 4000,
          isClosable: true,
        });
      }
    })();
  }, [dirtyObjects]);

  useEffect(() => {
    if (dirtyObjects && ownBookingsData && objectSelected) {
      const updatedObjectSelected = ownBookingsData?.data.result.bookingsData.find(
        object => object.Object === objectSelected.Object,
      );
      updatedObjectSelected && handleObjectSelected(updatedObjectSelected);
      handleDirtyObjects(false);
    }
  }, [ownBookingsData]);

  return (
    <>
      <TopNavigation handleLogout={handleLogout} />
      <Flex
        pt={{ base: 16, sm: 24 }}
        pb={16}
        minH="100vh"
        flexDirection={'column'}
        alignItems={'flex-start'}
        gridGap={8}
        overflow={'hidden'}>
        {ownBookingsData && objectSelected && (
          <>
            <VStack alignItems={'flex-start'} width={'100%'}>
              <SelectObject
                handleObjectSelected={handleObjectSelected}
                objectSelected={objectSelected}
                ownBookingsData={ownBookingsData?.data.result.bookingsData}
              />
            </VStack>
            <VStack
              p={4}
              borderRadius={{ sm: 0, md: 0, lg: 0, xl: 4 }}
              borderWidth={1}
              borderColor={'gray.300'}
              backgroundColor={'white'}
              width={'100%'}
              alignItems={'flex-start'}
              flexWrap={{ base: 'wrap', md: 'unset' }}>
              <VStack alignItems={'baseline'} width={'100%'} gridGap={4}>
                <Text w={'100%'} textAlign={'left'} fontSize={'xl'} textStyle={'category.primary'}>
                  Belegungskalender
                </Text>
                <HStack width={'100%'}>
                  <YearPicker
                    handleSelectedYear={handleSelectedYear}
                    selectedYear={selectedYear}
                    START_YEAR={START_YEAR}
                  />
                  <MonthPicker
                    handleSelectedMonth={handleSelectedMonth}
                    selectedMonth={selectedMonth}
                    START_MONTH={START_MONTH}
                  />
                </HStack>
                <BookingBigCalendar
                  ownBookingsData={ownBookingsData?.data.result.bookingsData}
                  objectSelected={objectSelected}
                  selectedMonth={selectedMonth}
                  selectedYear={selectedYear}
                  handleSelectedYear={handleSelectedYear}
                  handleSelectedMonth={handleSelectedMonth}
                />
              </VStack>
            </VStack>

            <Box
              p={4}
              borderRadius={{ sm: 0, md: 0, lg: 0, xl: 4 }}
              borderWidth={1}
              borderColor={'gray.300'}
              backgroundColor={'white'}
              width={'100%'}>
              <BookingsForm
                objectsParams={objectsParams}
                handleDateRange={handleDateRange}
                handleDirtyObjects={handleDirtyObjects}
                objectSelected={objectSelected}
                handleTokenState={handleTokenState}
                bookingEditParams={bookingEditParams}
                handleBookingEditParams={handleBookingEditParams}
              />
            </Box>

            <Box
              p={4}
              borderRadius={{ sm: 0, md: 0, lg: 0, xl: 4 }}
              borderWidth={1}
              borderColor={'gray.300'}
              backgroundColor={'white'}
              width={'100%'}
              h={{ base: 'calc(100% - 56px)', md: 'unset' }}
              overflow={'auto'}>
              <TableBookings
                objectSelected={objectSelected}
                handleDateRange={handleDateRange}
                objectsParams={objectsParams}
                handleDirtyObjects={handleDirtyObjects}
                handleTokenState={handleTokenState}
                handleBookingEditParams={handleBookingEditParams}
              />
            </Box>
          </>
        )}
      </Flex>
    </>
  );
};
